import React, { forwardRef, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _, { find } from 'lodash';
import { FieldTypes } from 'utils/formUtils';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { Form } from 'antd';
import { parseSelectOptions } from 'utils/formUtils';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { EmptyCancellableRequest } from 'restAPI';

export const STAGE_OPTIONS = [
  { label: "Build", value: "build" },
  { label: "Stage Release", value: "stage-release" },
  { label: "Release", value: "release" },
  { label: "Operate", value: "operate" },
  { label: "Develop", value: "develop" },
  { label: "Source", value: "source" },
  { label: "Proxy", value: "proxy" },
];

const SonatypeLifecycleMappingForm = forwardRef(function SonatypeLifecycleMappingForm({disableProjectFields = false, ...props}, ref) {
  const editModeRef = useRef(!_.isEmpty(props.record));
  const [form] = Form.useForm();
  const featureFlags = useFeatureFlag();
  const [organisationList, setOrganisationList] = useState(() => {
    if (props.record) {
      const { organisationId, organisationName } = props.record;
      return [{id: organisationId, name: organisationName}]
    }
    return [];
  });
  const [applicationList, setApplicationList] = useState(() => {
    if(props.record && !_.isNull(props.record.organisationId)){
        const { organisationId, applicationId, applicationName} = props.record;
        return {
            [organisationId]: [{id: applicationId, name: applicationName}]
        }
    }
    return {}
  });
  const [organisation,setOrganisation] = useState(() => {
    if(props.record){
        const { organisationId } = props.record;
        return organisationId
    };
    return ''
  });
  const paramAPIRef = useRef({});

  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      setOrganisationList(projects);
    }).catch(() => {
      message.error('Problem fetching organizations');
    });

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchStage = useCallback((params) => {
    if (!params?.project) {
      return EmptyCancellableRequest;
    }
    let { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...params });
    promise = promise.then((res) => res.projects);
    return { promise, cancel };
  }, [props.loginConfigId, props.toolName, ]);

  const getExtraFieldsData = useCallback((config) => {
    const { organisationId, applicationId, stage } = config;
    const organisationName = find(organisationList, (obj) => obj.id === organisationId)?.name;
    const applicationName = find(applicationList[organisationId], (obj) => obj.id === applicationId)?.name;
    let appStage = undefined;
    if(featureFlags.NEXUS_LIFECYCLE_MULTI_STAGE_REPORT_SUPPORT){
      appStage = `${applicationId}:${stage}`;
    }
    return {
      organisationId,
      organisationName,
      applicationId,
      applicationName,
      stage,
      appStage,
      ...(config.discovery_config_id && {
        discovery_config_id: config.discovery_config_id,
      }),
    };
  },[organisationList, applicationList, featureFlags.NEXUS_LIFECYCLE_MULTI_STAGE_REPORT_SUPPORT]);

  const onOrganizationChange = useCallback((value, field) => {
    setOrganisation(value)
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      applicationId: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);
  
  const onApplicationChange = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      stage: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const fetchApplications = useCallback((organisation) => {
    if((editModeRef.current && organisation) || (organisation && !applicationList[organisation])){
      editModeRef.current = false;
      const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, 
        { login_id: props.loginConfigId, organisation});

        promise.then(response=> {
          const { projects } = response;
          setApplicationList((orgList) =>({...orgList, [organisation]: projects}));
        }).catch(()=>{
        message.error('Problem fetching projects')
        })

        return cancel;
    }
  }, [applicationList, props.toolName, props.loginConfigId])

  useEffect(() => fetchApplications(organisation), [organisation, fetchApplications]);

  const extraFields = useMemo(() => ([
    {
      name: "organisationId",
      colSpan:  featureFlags.NEXUS_LIFECYCLE_MULTI_STAGE_REPORT_SUPPORT ? 8 : 12,
      label: "Organization",
      required: true,
      type: FieldTypes.SELECT,
      disabled: disableProjectFields,
      options: organisationList,
      props: {
        autoFocus: !disableProjectFields,
        onChange: onOrganizationChange
      }
    },
    {
      name: 'applicationId',
      colSpan:  featureFlags.NEXUS_LIFECYCLE_MULTI_STAGE_REPORT_SUPPORT ? 8 : 12,
      type: FieldTypes.SELECT,
      label: "Application",
      disabled: disableProjectFields,
      required: true,
      props: {
        getFormListOptions: (fieldKey) => {
          const configs = form.getFieldValue("configurations")
          const orgId = configs[fieldKey]?.organisationId
          return orgId ? parseSelectOptions(applicationList[orgId])  : []
        },
        onChange: onApplicationChange
      }
    }, 
    ...(featureFlags.NEXUS_LIFECYCLE_MULTI_STAGE_REPORT_SUPPORT ? [{
      name: "stage",
      label: "Stage",
      colSpan: 8,
      required: true,
      type: FieldTypes.SELECT,
      disabled: disableProjectFields,
      props: {
        api: fetchStage,
        setExtraProps: (fieldProps, currentField) => {
          const configurations = form.getFieldValue("configurations");
          const currentConfiguration = configurations[currentField.name] || {};
          const { applicationId } = currentConfiguration;
          if (applicationId && !paramAPIRef.current[applicationId]) {
            paramAPIRef.current[applicationId] = { project: currentConfiguration.applicationId };
          }
          return {
            apiParams: paramAPIRef.current[applicationId],
          }
        },
      }
    }] : [])
  ]), [organisationList, disableProjectFields, onOrganizationChange, applicationList, form, featureFlags.NEXUS_LIFECYCLE_MULTI_STAGE_REPORT_SUPPORT, fetchStage, onApplicationChange]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { organisationId, applicationId, stage } = config;
    return {  
      organisationId,
      applicationId,
      stage: stage,
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
   };
    }, []);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      {...props}
    />
  )
})

export default SonatypeLifecycleMappingForm;
